declare global {
  var newrelic: {
    noticeError: (error: Error, attributes?: Record<string, any>) => void;
    recordCustomEvent?: (eventType: string, attributes?: Record<string, any>) => void;
  } | undefined
}

// Environment validation utility
export const requiredEnvVars = [
  'NEXT_PUBLIC_SUPABASE_URL',
  'NEXT_PUBLIC_SUPABASE_ANON_KEY',
  'SUPABASE_SERVICE_ROLE_KEY',
  'NEXT_PUBLIC_SANITY_PROJECT_ID',
] as const

/**
 * Check if an environment variable exists either in process.env or runtime config
 */
export function hasEnvVar(name: string): boolean {
  // Check process.env first (works in all environments)
  if (process.env[name]) return true;
  
  // Check runtime config in browser environment
  if (typeof window !== 'undefined' && 
      window.__RUNTIME_CONFIG__ && 
      window.__RUNTIME_CONFIG__[name as keyof typeof window.__RUNTIME_CONFIG__]) {
    return true;
  }
  
  return false;
}

export function isProduction(): boolean {
  return process.env.NODE_ENV === 'production'
}

export function isDevelopment(): boolean {
  return process.env.NODE_ENV === 'development'
}

function captureError(error: Error): void {
  // Log to console in development
  console.error(error)

  // In production, also send to error monitoring
  if (isProduction()) {
    // Send to New Relic if available
    if (global.newrelic) {
      global.newrelic.noticeError(error)
    }
    
    // Log to CloudWatch through console in AWS
    console.error(JSON.stringify({
      level: 'ERROR',
      message: error.message,
      name: error.name,
      stack: error.stack,
      timestamp: new Date().toISOString()
    }))
  }
}

export function validateEnv(): void {
  try {
    // Always try to use runtime config in browser
    if (typeof window !== 'undefined') {
      // Check if we have runtime config available
      if (window.__RUNTIME_CONFIG__) {
        console.debug('Runtime configuration found in browser context');
        return; // Skip further validation if runtime config exists
      } else {
        console.warn('Runtime configuration not found in browser context');
        // We'll continue with validation in case we're in development with env vars
      }
    }
    
    // Check for missing vars using our hasEnvVar helper
    const missingVars = requiredEnvVars.filter(name => !hasEnvVar(name))

    if (missingVars.length > 0) {
      // In development, throw an error
      if (isDevelopment()) {
        throw new Error(
          `Missing required environment variables:\n${missingVars.join('\n')}\n` +
          'Make sure these are set in your .env.local file or deployment environment.'
        )
      } else {
        // In production, just log a warning
        console.warn(`Missing environment variables: ${missingVars.join(', ')}. ` +
          'Some features may not work correctly.')
      }
    }

    // Validate localhost URLs in production
    if (isProduction()) {
      const baseUrl = process.env.NEXT_PUBLIC_BASE_URL
      if (baseUrl && (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1'))) {
        console.warn(
          'NEXT_PUBLIC_BASE_URL is set to localhost in production environment.\n' +
          'Update this in your deployment environment variables.'
        )
      }
    }
  } catch (error) {
    // Capture the error but only throw in development
    captureError(error as Error)
    if (isDevelopment()) {
      throw error
    }
  }
}

export function getRedirectUrl(path: string): string {
  try {
    let baseUrl: string | undefined;
    
    // In production, always use the production domain to avoid localhost issues
    if (process.env.NODE_ENV === 'production') {
      baseUrl = 'https://fintechtoronto.com';
    } else if (typeof window !== 'undefined') {
      // In development, use the window location
      baseUrl = window.location.origin;
    } else {
      // Fallback to env var if available
      baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
    }

    if (!baseUrl) {
      console.warn('No base URL available, using fallback production URL');
      baseUrl = 'https://fintechtoronto.com';
    }

    const safePath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${safePath}`;
  } catch (error) {
    captureError(error as Error);
    console.warn('Error in getRedirectUrl, using fallback URL');
    
    // In production, return a hardcoded URL to ensure it works
    if (process.env.NODE_ENV === 'production') {
      return `https://fintechtoronto.com${path.startsWith('/') ? path : `/${path}`}`;
    }
    
    return path.startsWith('/') ? path : `/${path}`;
  }
}